import type { ActionFunctionArgs } from "@remix-run/node";

import { json } from "@remix-run/node";
import { Form, Link, useActionData, useNavigation } from "@remix-run/react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { Heading, Region } from "~/components/heading";
import { i18n } from "~/services/i18n.server";
import type { StaticAlertProps } from "~/ui/shadcn/alert";
import { StaticAlert } from "~/ui/shadcn/alert";
import { Button } from "~/ui/shadcn/button";
import { Field, FieldLabel } from "~/ui/shadcn/field";
import { Input } from "~/ui/shadcn/input";
import { requireEnv } from "~/utils/environment";
import { toStatic } from "~/utils/toStatic";

export async function action({ request }: ActionFunctionArgs) {
  let t = await i18n.getFixedT(request);

  try {
    let formData = await request.formData();

    let url = getIssuerURL();
    url.pathname = "/dbconnections/change_password";

    let body = z
      .object({
        client_id: z.string(),
        email: z
          .string({
            required_error: t("Enter your email address"),
          })
          .nonempty(t("Enter your email address"))
          .email({
            message: t(
              "Enter an email address in the correct format, like name@example.com",
            ),
          }),
        connection: z.string(),
      })
      .parse({
        client_id: requireEnv("AUTH0_CLIENT_ID"),
        email: formData.get("email"),
        connection: "Username-Password-Authentication",
      });

    await fetch(url, {
      method: "POST",
      body: JSON.stringify(body),
      headers: { "Content-Type": "application/json" },
    });

    return json<StaticAlertProps>({
      type: "success",
      title: t("Success") as string,
      body: t(
        "We have sent you an email with instructions to reset your password.",
      ) as string,
    });
  } catch (error) {
    if (error instanceof z.ZodError) {
      return json<StaticAlertProps>({
        type: "danger",
        title: t("There is a problem") as string,
        body: error.issues.map((issue) => issue.message),
      });
    }
    throw error;
  }
}

export default function Component() {
  let { t } = useTranslation();
  let navigation = useNavigation();
  let isSubmitting = navigation.state !== "idle";
  let actionData = useActionData<StaticAlertProps | undefined>() as
    | StaticAlertProps
    | undefined;
  return (
    <div className="min-h-screen w-full scheme-light bg-muted md:py-24 flex md:items-center md:justify-center -md:flex-col">
      <figure>
        <img
          src={toStatic("/auth0-login/login-mobile.webp")}
          alt={t("Daffy - A smarter way to give")}
          className="md:hidden"
        />
        <img
          src={toStatic("/auth0-login/login-desktop-2x.webp")}
          alt={t("Daffy - A smarter way to give")}
          className="w-full max-w-130 -md:hidden"
        />
      </figure>

      <Region className="pt-8 px-5 pb-11 md:px-15 md:py-13.5 rounded-xl shadow max-w-130 flex flex-col gap-6 md:gap-8 bg-card">
        <header className="-md:text-center flex flex-col gap-2">
          <Heading className="text-3xl leading-9 font-medium">
            {t("Forgot Your Password?")}
          </Heading>

          <p className="text-muted-foreground text-sm leading-5">
            {t(
              "Enter your email address and we will send you instructions to reset your password.",
            )}
          </p>
        </header>

        <Form method="post" className="contents">
          <Field>
            <FieldLabel htmlFor="email">{t("Email address")}</FieldLabel>
            <Input id="email" type="email" name="email" />
          </Field>

          {actionData ? <StaticAlert {...actionData} /> : null}

          <Button
            type="submit"
            name="intent"
            value="resetPassword"
            disabled={isSubmitting}
          >
            {t("Continue")}
          </Button>

          <footer className="flex text-sm leading-5 gap-3 justify-center">
            <Link to="/login" className="font-medium" prefetch="intent">
              {t("Back to Daffy for Work")}
            </Link>
          </footer>
        </Form>
      </Region>
    </div>
  );
}

// This function returns the URL of the issuer for the Auth0 authentication service.
// The issuer URL is taken from the AUTH0_ISSUER_BASE_URL environment variable.
function getIssuerURL() {
  return new URL(`https://${requireEnv("AUTH0_ISSUER_BASE_URL")}`);
}
